import React, { useEffect, useRef, useState } from 'react';
import { buildStyles } from './manualBookUi';

export default function ManualBookShell({
  manualPages,
  renderManualPage,
  pdfFileName,
  pdfPreviewTitle
}) {
  const [viewportWidth, setViewportWidth] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth : 1280
  ));
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [turnDirection, setTurnDirection] = useState('next');
  const manualContentRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = viewportWidth < 640;
  const isTablet = viewportWidth < 980;
  const isSinglePageView = isTablet;
  const styles = buildStyles({ isMobile, isTablet });

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const styleId = 'manual-book-animations';
    let styleTag = document.getElementById(styleId);
    let created = false;

    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = styleId;
      created = true;
    }

    styleTag.textContent = `
      @keyframes manualBookTurnNext {
        0% { opacity: 0; transform: translateX(20px) rotateY(-7deg) scale(0.985); }
        100% { opacity: 1; transform: translateX(0) rotateY(0deg) scale(1); }
      }
      @keyframes manualBookTurnPrev {
        0% { opacity: 0; transform: translateX(-20px) rotateY(7deg) scale(0.985); }
        100% { opacity: 1; transform: translateX(0) rotateY(0deg) scale(1); }
      }
    `;

    if (created) document.head.appendChild(styleTag);

    return () => {
      if (created && styleTag?.parentNode) styleTag.parentNode.removeChild(styleTag);
    };
  }, []);

  const pagesPerView = isSinglePageView ? 1 : 2;
  const maxPageIndex = Math.max(0, manualPages.length - pagesPerView);

  useEffect(() => {
    setActivePageIndex((prev) => {
      if (isSinglePageView) return Math.min(prev, manualPages.length - 1);
      const normalized = Math.min(prev, Math.max(0, manualPages.length - 2));
      return normalized % 2 === 0 ? normalized : normalized - 1;
    });
  }, [isSinglePageView, manualPages.length]);

  const goToPage = (index) => {
    const normalizedTarget = isSinglePageView ? index : index - (index % 2);
    const clamped = Math.max(0, Math.min(normalizedTarget, maxPageIndex));
    setTurnDirection(clamped >= activePageIndex ? 'next' : 'prev');
    setActivePageIndex(clamped);
  };

  const goNextSpread = () => {
    if (activePageIndex >= maxPageIndex) return;
    setTurnDirection('next');
    setActivePageIndex((prev) => Math.min(prev + pagesPerView, maxPageIndex));
  };

  const goPrevSpread = () => {
    if (activePageIndex <= 0) return;
    setTurnDirection('prev');
    setActivePageIndex((prev) => Math.max(prev - pagesPerView, 0));
  };

  const visiblePages = manualPages.slice(activePageIndex, activePageIndex + pagesPerView);
  const spreadCount = Math.ceil(manualPages.length / pagesPerView);
  const currentSpread = Math.floor(activePageIndex / pagesPerView) + 1;

  const handleExportPdf = async () => {
    if (!manualContentRef.current || isExportingPdf || typeof window === 'undefined') return;

    const previewWindow = window.open('', '_blank');

    try {
      setIsExportingPdf(true);

      if (previewWindow) {
        previewWindow.document.write(`
          <html>
            <head>
              <title>${pdfPreviewTitle}</title>
              <style>
                html, body {
                  margin: 0;
                  height: 100%;
                  background: #0f172a;
                  font-family: Arial, sans-serif;
                }
                .manual-pdf-loading {
                  height: 100%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: #e2e8f0;
                  letter-spacing: 0.02em;
                }
              </style>
            </head>
            <body>
              <div class="manual-pdf-loading">Generando vista previa del PDF...</div>
            </body>
          </html>
        `);
        previewWindow.document.close();
      }

      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf')
      ]);

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const exportPages = Array.from(
        manualContentRef.current.querySelectorAll('[data-export-manual-page="true"]')
      );

      for (let index = 0; index < exportPages.length; index += 1) {
        const exportPage = exportPages[index];
        const canvas = await html2canvas(exportPage, {
          scale: Math.max(2, window.devicePixelRatio || 1),
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
          windowWidth: exportPage.scrollWidth,
          windowHeight: exportPage.scrollHeight
        });

        const imgData = canvas.toDataURL('image/png');

        if (index > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
      }

      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);

      if (!previewWindow) {
        pdf.save(pdfFileName);
      } else {
        previewWindow.document.open();
        previewWindow.document.write(`
          <html>
            <head>
              <title>${pdfPreviewTitle}</title>
              <style>
                html, body {
                  margin: 0;
                  height: 100%;
                  background: #111827;
                }
                iframe {
                  border: 0;
                  width: 100%;
                  height: 100%;
                  display: block;
                  background: #111827;
                }
              </style>
            </head>
            <body>
              <iframe src="${pdfUrl}" title="${pdfPreviewTitle}"></iframe>
            </body>
          </html>
        `);
        previewWindow.document.close();
        window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60_000);
      }
    } catch (error) {
      console.error('Error exportando PDF del manual:', error);
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
    } finally {
      setIsExportingPdf(false);
    }
  };

  return (
    <div style={{
      flex: 1,
      minHeight: '100%',
      overflowY: 'visible',
      overflowX: 'hidden',
      background: 'radial-gradient(circle at top left, rgba(35, 101, 171, 0.22), transparent 30%), radial-gradient(circle at top right, rgba(95, 151, 221, 0.16), transparent 26%), linear-gradient(180deg, #f8fbff 0%, #edf4ff 100%)',
      padding: isMobile ? '16px 12px 112px' : '28px clamp(18px, 3vw, 38px) 124px'
    }}>
      <div style={styles.bookShell}>
        <div style={styles.topControlRow}>
          <div style={styles.chapterDeck}>
            <div style={styles.chapterDeckHeader}>
              <div>
                <div style={styles.chapterDeckKicker}>Edición navegable</div>
                <div style={styles.chapterDeckTitle}>Capítulos del manual</div>
              </div>
              <div style={styles.spreadCounter}>Spread {String(currentSpread).padStart(2, '0')} / {String(spreadCount).padStart(2, '0')}</div>
            </div>
            <div style={styles.chapterRailWrap}>
              <div style={styles.chapterRailFadeLeft} />
              <div style={styles.chapterRail}>
                {manualPages.map((page, index) => {
                  const isActive = visiblePages.some((visiblePage) => visiblePage.id === page.id);
                  return (
                    <button
                      key={page.id}
                      type="button"
                      onClick={() => goToPage(index)}
                      style={{
                        ...styles.chapterPill,
                        ...(isActive ? styles.chapterPillActive : null)
                      }}
                    >
                      <span style={styles.chapterPillNumber}>{String(index + 1).padStart(2, '0')}</span>
                      <span>{page.label}</span>
                    </button>
                  );
                })}
              </div>
              <div style={styles.chapterRailFadeRight} />
            </div>
          </div>
          <button
            type="button"
            onClick={handleExportPdf}
            disabled={isExportingPdf}
            style={styles.pdfActionButton}
          >
            {isExportingPdf ? 'Generando PDF...' : 'Exportar PDF'}
          </button>
        </div>

        <div style={styles.bookStage}>
          <div style={styles.bookAura} />
          {!isSinglePageView && <div style={styles.bookSpine} />}
          <div
            key={`${activePageIndex}-${pagesPerView}`}
            style={{
              ...styles.bookSpread,
              gridTemplateColumns: isSinglePageView ? '1fr' : '1fr 1fr',
              animationName: turnDirection === 'next' ? 'manualBookTurnNext' : 'manualBookTurnPrev'
            }}
          >
            {visiblePages.map((page, index) => (
              <article key={page.id} style={{ ...styles.bookPage, ...(page.id === 'portada' ? styles.bookPageCover : null), ...(index === 0 && !isSinglePageView ? styles.bookPageLeft : styles.bookPageRight) }}>
                <div style={page.id === 'portada' ? styles.pageInnerGlowCover : styles.pageInnerGlow} />
                {page.id !== 'portada' && !isMobile && <div style={styles.pageCornerFold} />}
                {page.id !== 'portada' && (
                  <>
                    <div style={styles.pageChromeTop}>
                      <span style={styles.pageEyebrow}>{page.eyebrow}</span>
                      <span style={styles.pageNumber}>{String(activePageIndex + index + 1).padStart(2, '0')}</span>
                    </div>
                    <h2 style={styles.pageTitle}>{page.title}</h2>
                  </>
                )}
                <div style={{ ...styles.pageBody, ...(page.id === 'portada' ? styles.pageBodyCover : null) }}>
                  {renderManualPage(page.id, styles, { isMobile, isTablet, forExport: false, manualPages })}
                </div>
              </article>
            ))}
          </div>
        </div>

        <div style={styles.bookNavRow}>
          <button type="button" onClick={goPrevSpread} disabled={activePageIndex <= 0} style={{ ...styles.bookNavButton, ...(activePageIndex <= 0 ? styles.bookNavButtonDisabled : null) }}>
            <span style={styles.bookNavArrow}>←</span>
            <span style={styles.bookNavButtonText}>Hoja anterior</span>
          </button>
          <div style={styles.bookNavHint}>Navegación por spreads y capítulos</div>
          <button type="button" onClick={goNextSpread} disabled={activePageIndex >= maxPageIndex} style={{ ...styles.bookNavButton, ...(activePageIndex >= maxPageIndex ? styles.bookNavButtonDisabled : null) }}>
            <span style={styles.bookNavButtonText}>Hoja siguiente</span>
            <span style={styles.bookNavArrow}>→</span>
          </button>
        </div>
      </div>

      <div ref={manualContentRef} style={styles.exportRoot}>
        {manualPages.map((page, index) => (
          <section key={page.id} data-export-manual-page="true" style={{ ...styles.exportPage, ...(page.id === 'portada' ? styles.exportPageCover : null) }}>
            {page.id !== 'portada' && (
              <>
                <div style={styles.exportPageHeader}>
                  <span style={styles.exportEyebrow}>{page.eyebrow}</span>
                  <span style={styles.exportPageNumber}>{String(index + 1).padStart(2, '0')}</span>
                </div>
                <h2 style={styles.exportPageTitle}>{page.title}</h2>
              </>
            )}
            {renderManualPage(page.id, styles, { isMobile: false, isTablet: false, forExport: true, manualPages })}
          </section>
        ))}
      </div>
    </div>
  );
}
